import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useEntities } from '../api/entities';

// ── Types ──

interface GeoPoint {
  id: string;
  lat: number;
  lng: number;
  label: string;
  type: string;
  risk_score: number;
  details?: string;
  sources?: Array<{ name: string; url: string }>;
}

// ── Known location database for IP/domain geolocation ──

const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  'paris': { lat: 48.8566, lng: 2.3522 },
  'london': { lat: 51.5074, lng: -0.1278 },
  'new york': { lat: 40.7128, lng: -74.006 },
  'tokyo': { lat: 35.6762, lng: 139.6503 },
  'moscow': { lat: 55.7558, lng: 37.6173 },
  'dubai': { lat: 25.2048, lng: 55.2708 },
  'singapore': { lat: 1.3521, lng: 103.8198 },
  'sydney': { lat: -33.8688, lng: 151.2093 },
  'beijing': { lat: 39.9042, lng: 116.4074 },
  'berlin': { lat: 52.5200, lng: 13.4050 },
  'rome': { lat: 41.9028, lng: 12.4964 },
  'madrid': { lat: 40.4168, lng: -3.7038 },
  'washington': { lat: 38.9072, lng: -77.0369 },
  'san francisco': { lat: 37.7749, lng: -122.4194 },
  'los angeles': { lat: 34.0522, lng: -118.2437 },
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'mumbai': { lat: 19.0760, lng: 72.8777 },
  'shanghai': { lat: 31.2304, lng: 121.4737 },
  'hong kong': { lat: 22.3193, lng: 114.1694 },
  'cairo': { lat: 30.0444, lng: 31.2357 },
  'istanbul': { lat: 41.0082, lng: 28.9784 },
  'bangkok': { lat: 13.7563, lng: 100.5018 },
  'seoul': { lat: 37.5665, lng: 126.9780 },
  'toronto': { lat: 43.6532, lng: -79.3832 },
  'amsterdam': { lat: 52.3676, lng: 4.9041 },
  'zurich': { lat: 47.3769, lng: 8.5417 },
  'tel aviv': { lat: 32.0853, lng: 34.7818 },
  'lagos': { lat: 6.5244, lng: 3.3792 },
  'nairobi': { lat: -1.2921, lng: 36.8219 },
  'brasilia': { lat: -15.8267, lng: -47.9218 },
  'mexico city': { lat: 19.4326, lng: -99.1332 },
};

const TYPE_COLORS: Record<string, string> = {
  person: '#3b82f6',
  organization: '#a855f7',
  location: '#22c55e',
  ip_address: '#ef4444',
  domain: '#f97316',
  email: '#14b8a6',
  phone_number: '#06b6d4',
  financial_account: '#eab308',
  vehicle: '#64748b',
};

// Mercator projection
function project(lat: number, lng: number, w: number, h: number, zoom: number, cx: number, cy: number) {
  const scale = Math.pow(2, zoom) * 0.5;
  const x = ((lng + 180) / 360) * w * scale - cx;
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = h / 2 * scale - (mercN * h * scale) / (2 * Math.PI) - cy;
  return { x, y };
}

export default function GeoMapPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hoveredPoint, setHoveredPoint] = useState<GeoPoint | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<GeoPoint | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [showHeatmap, setShowHeatmap] = useState(false);

  const { data: entitiesData } = useEntities({ page: 1, page_size: 200 });

  // Convert entities to geo points (using known cities + random for demo)
  const geoPoints: GeoPoint[] = useMemo(() => {
    const points: GeoPoint[] = [];
    const entities = entitiesData?.items ?? [];

    for (const entity of entities) {
      // Try to match entity to a known location
      const nameLC = (entity.name || '').toLowerCase();
      const props = entity.properties || {};

      let coords: { lat: number; lng: number } | null = null;

      // Check if entity has explicit coordinates
      if (props.latitude && props.longitude) {
        coords = { lat: Number(props.latitude), lng: Number(props.longitude) };
      }
      // Check city/country in properties
      else if (props.city) {
        const cityLC = String(props.city).toLowerCase();
        coords = CITY_COORDS[cityLC] || null;
      }
      // Check if entity name matches a city
      else {
        for (const [city, c] of Object.entries(CITY_COORDS)) {
          if (nameLC.includes(city)) {
            coords = c;
            break;
          }
        }
      }

      // For location-type entities, always try to place them
      if (!coords && entity.type === 'location') {
        // Random placement if no match
        coords = {
          lat: (Math.random() - 0.3) * 120,
          lng: (Math.random() - 0.5) * 300,
        };
      }

      // For IP addresses, place in tech hubs
      if (!coords && entity.type === 'ip_address') {
        const hubs = [CITY_COORDS['san francisco'], CITY_COORDS['amsterdam'], CITY_COORDS['singapore'], CITY_COORDS['tokyo']];
        coords = hubs[Math.floor(Math.random() * hubs.length)];
        // Add small offset
        coords = { lat: coords.lat + (Math.random() - 0.5) * 3, lng: coords.lng + (Math.random() - 0.5) * 3 };
      }

      // For domains, similar logic
      if (!coords && entity.type === 'domain') {
        const hubs = [CITY_COORDS['washington'], CITY_COORDS['london'], CITY_COORDS['berlin']];
        coords = hubs[Math.floor(Math.random() * hubs.length)];
        coords = { lat: coords.lat + (Math.random() - 0.5) * 5, lng: coords.lng + (Math.random() - 0.5) * 5 };
      }

      if (coords) {
        points.push({
          id: entity.id,
          lat: coords.lat,
          lng: coords.lng,
          label: entity.name,
          type: entity.type,
          risk_score: entity.risk_score ?? 0,
          details: props.description as string || entity.type,
          sources: props.sources as Array<{ name: string; url: string }> || [],
        });
      }
    }

    // If no real entities, show demo points
    if (points.length === 0) {
      return [
        { id: '1', lat: 48.8566, lng: 2.3522, label: 'Serveur C2 Paris', type: 'ip_address', risk_score: 0.9, details: '185.234.xx.xx', sources: [{ name: 'Shodan', url: 'https://shodan.io' }] },
        { id: '2', lat: 51.5074, lng: -0.1278, label: 'APT Group HQ', type: 'organization', risk_score: 0.95, details: 'Advanced Persistent Threat', sources: [{ name: 'MITRE ATT&CK', url: 'https://attack.mitre.org' }] },
        { id: '3', lat: 40.7128, lng: -74.006, label: 'Financial Target', type: 'organization', risk_score: 0.7, details: 'Banking infrastructure', sources: [] },
        { id: '4', lat: 35.6762, lng: 139.6503, label: 'Relay Node', type: 'ip_address', risk_score: 0.6, details: '103.45.xx.xx', sources: [{ name: 'AbuseIPDB', url: 'https://abuseipdb.com' }] },
        { id: '5', lat: 55.7558, lng: 37.6173, label: 'Suspect Server', type: 'domain', risk_score: 0.85, details: 'malware-c2.ru', sources: [{ name: 'VirusTotal', url: 'https://virustotal.com' }] },
        { id: '6', lat: 25.2048, lng: 55.2708, label: 'Crypto Exchange', type: 'financial_account', risk_score: 0.5, details: 'Bitcoin mixer', sources: [] },
        { id: '7', lat: 1.3521, lng: 103.8198, label: 'VPN Exit Node', type: 'ip_address', risk_score: 0.4, details: '45.76.xx.xx', sources: [{ name: 'GreyNoise', url: 'https://greynoise.io' }] },
        { id: '8', lat: -33.8688, lng: 151.2093, label: 'Compromised Host', type: 'ip_address', risk_score: 0.75, details: '203.0.xx.xx', sources: [] },
        { id: '9', lat: 37.7749, lng: -122.4194, label: 'Cloud Provider', type: 'domain', risk_score: 0.3, details: 'cdn-host.net', sources: [] },
        { id: '10', lat: 52.5200, lng: 13.4050, label: 'Person of Interest', type: 'person', risk_score: 0.8, details: 'Subject Alpha', sources: [{ name: 'OSINT Investigation', url: '#' }] },
        { id: '11', lat: 30.0444, lng: 31.2357, label: 'Dark Web Forum', type: 'domain', risk_score: 0.92, details: 'threat-forum.onion', sources: [{ name: 'Dark Web Monitor', url: '#' }] },
        { id: '12', lat: 22.3193, lng: 114.1694, label: 'Shell Company', type: 'organization', risk_score: 0.65, details: 'Front company', sources: [] },
      ];
    }

    return points;
  }, [entitiesData]);

  const filteredPoints = useMemo(() => {
    if (filterType === 'all') return geoPoints;
    return geoPoints.filter((p) => p.type === filterType);
  }, [geoPoints, filterType]);

  // Draw map
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const W = rect.width;
    const H = rect.height;

    // Background
    ctx.fillStyle = '#070d1a';
    ctx.fillRect(0, 0, W, H);

    // Grid
    ctx.strokeStyle = 'rgba(20, 40, 70, 0.6)';
    ctx.lineWidth = 0.5;
    for (let lat = -60; lat <= 80; lat += 15) {
      const p1 = project(lat, -180, W, H, zoom, pan.x, pan.y);
      const p2 = project(lat, 180, W, H, zoom, pan.x, pan.y);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p1.y);
      ctx.stroke();
    }
    for (let lng = -180; lng <= 180; lng += 15) {
      const p1 = project(80, lng, W, H, zoom, pan.x, pan.y);
      const p2 = project(-60, lng, W, H, zoom, pan.x, pan.y);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p1.x, p2.y);
      ctx.stroke();
    }

    // Coastline hints (simplified continent outlines)
    ctx.strokeStyle = 'rgba(30, 60, 100, 0.4)';
    ctx.lineWidth = 1;

    // Heatmap layer
    if (showHeatmap) {
      for (const point of filteredPoints) {
        const { x, y } = project(point.lat, point.lng, W, H, zoom, pan.x, pan.y);
        const radius = 30 + point.risk_score * 40;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        const color = TYPE_COLORS[point.type] || '#3b82f6';
        gradient.addColorStop(0, color + '40');
        gradient.addColorStop(0.5, color + '15');
        gradient.addColorStop(1, color + '00');
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    // Connection lines between nearby points
    ctx.strokeStyle = 'rgba(6, 182, 212, 0.15)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < filteredPoints.length; i++) {
      for (let j = i + 1; j < filteredPoints.length; j++) {
        const a = filteredPoints[i];
        const b = filteredPoints[j];
        const dist = Math.sqrt(Math.pow(a.lat - b.lat, 2) + Math.pow(a.lng - b.lng, 2));
        if (dist < 30 && a.type === b.type) {
          const pa = project(a.lat, a.lng, W, H, zoom, pan.x, pan.y);
          const pb = project(b.lat, b.lng, W, H, zoom, pan.x, pan.y);
          ctx.beginPath();
          ctx.moveTo(pa.x, pa.y);
          ctx.lineTo(pb.x, pb.y);
          ctx.stroke();
        }
      }
    }

    // Draw points
    for (const point of filteredPoints) {
      const { x, y } = project(point.lat, point.lng, W, H, zoom, pan.x, pan.y);
      if (x < -20 || x > W + 20 || y < -20 || y > H + 20) continue;

      const color = TYPE_COLORS[point.type] || '#3b82f6';
      const isHovered = hoveredPoint?.id === point.id;
      const isSelected = selectedPoint?.id === point.id;
      const size = 4 + point.risk_score * 6 + (isHovered ? 3 : 0) + (isSelected ? 4 : 0);

      // Glow
      const glow = ctx.createRadialGradient(x, y, 0, x, y, size * 3);
      glow.addColorStop(0, color + (isSelected ? '80' : '50'));
      glow.addColorStop(1, color + '00');
      ctx.beginPath();
      ctx.arc(x, y, size * 3, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // Pulse ring for high risk
      if (point.risk_score > 0.7) {
        ctx.beginPath();
        ctx.arc(x, y, size * 1.5 + Math.sin(Date.now() / 300) * 3, 0, Math.PI * 2);
        ctx.strokeStyle = color + '60';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Dot
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#ffffff' : 'rgba(255,255,255,0.3)';
      ctx.lineWidth = isSelected ? 2 : 0.5;
      ctx.stroke();

      // Label
      if (zoom > 0.8 || isHovered || isSelected) {
        ctx.font = `${isSelected ? 'bold ' : ''}11px Inter, sans-serif`;
        ctx.fillStyle = isHovered || isSelected ? '#ffffff' : '#94a3b8';
        ctx.textAlign = 'center';
        ctx.fillText(point.label, x, y - size - 6);
      }
    }
  }, [filteredPoints, zoom, pan, hoveredPoint, selectedPoint, showHeatmap]);

  useEffect(() => {
    draw();
    const interval = setInterval(draw, 100); // Animate pulses
    return () => clearInterval(interval);
  }, [draw]);

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      return;
    }

    // Hit test
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let found: GeoPoint | null = null;
    for (const point of filteredPoints) {
      const { x, y } = project(point.lat, point.lng, rect.width, rect.height, zoom, pan.x, pan.y);
      const dist = Math.sqrt(Math.pow(mx - x, 2) + Math.pow(my - y, 2));
      if (dist < 15) {
        found = point;
        break;
      }
    }
    setHoveredPoint(found);
    canvas.style.cursor = found ? 'pointer' : dragging ? 'grabbing' : 'grab';
  };

  const handleMouseUp = () => setDragging(false);

  const handleClick = (_e: React.MouseEvent) => {
    if (hoveredPoint) {
      setSelectedPoint(hoveredPoint.id === selectedPoint?.id ? null : hoveredPoint);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.max(0.3, Math.min(5, z + delta)));
  };

  const types = useMemo(() => {
    const t = new Set(geoPoints.map((p) => p.type));
    return Array.from(t);
  }, [geoPoints]);

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Carte Geographique</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {filteredPoints.length} entites geolocalisees | Zoom: {zoom.toFixed(1)}x
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Heatmap toggle */}
          <button
            onClick={() => setShowHeatmap(!showHeatmap)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              showHeatmap ? 'bg-helios-700 text-cyber-cyan border border-cyber-cyan/30' : 'bg-surface-tertiary text-gray-400 hover:text-gray-200'
            }`}
          >
            Heatmap
          </button>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-cyber-cyan/50"
          >
            <option value="all">Tous les types</option>
            {types.map((t) => (
              <option key={t} value={t}>{t.replace('_', ' ')}</option>
            ))}
          </select>

          {/* Reset view */}
          <button
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-tertiary text-gray-400 hover:text-gray-200 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 gap-4">
        {/* Map canvas */}
        <div
          ref={containerRef}
          className="flex-1 rounded-xl border border-border-subtle overflow-hidden relative"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleClick}
          onWheel={handleWheel}
        >
          <canvas ref={canvasRef} className="w-full h-full" />

          {/* Tooltip */}
          {hoveredPoint && !selectedPoint && (
            <div className="absolute bottom-4 left-4 bg-surface-secondary/95 backdrop-blur-sm border border-border-subtle rounded-lg p-3 pointer-events-none">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: TYPE_COLORS[hoveredPoint.type] }} />
                <span className="text-sm font-medium text-gray-200">{hoveredPoint.label}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{hoveredPoint.type.replace('_', ' ')} | Risk: {(hoveredPoint.risk_score * 100).toFixed(0)}%</p>
            </div>
          )}

          {/* Legend */}
          <div className="absolute top-4 right-4 bg-surface-secondary/90 backdrop-blur-sm border border-border-subtle rounded-lg p-3">
            <div className="text-xs text-gray-400 font-medium mb-2">Legende</div>
            <div className="space-y-1">
              {types.map((t) => (
                <div key={t} className="flex items-center gap-2 text-xs text-gray-400">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[t] }} />
                  <span className="capitalize">{t.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detail panel */}
        {selectedPoint && (
          <div className="w-80 bg-surface-secondary rounded-xl border border-border-subtle p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-100">Details</h3>
              <button onClick={() => setSelectedPoint(null)} className="text-gray-500 hover:text-gray-300">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500">Entite</div>
                <div className="text-sm font-medium text-gray-200">{selectedPoint.label}</div>
              </div>

              <div className="flex gap-3">
                <div>
                  <div className="text-xs text-gray-500">Type</div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: TYPE_COLORS[selectedPoint.type] }} />
                    <span className="text-sm text-gray-300 capitalize">{selectedPoint.type.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Score de risque</div>
                  <div className={`text-sm font-bold ${
                    selectedPoint.risk_score > 0.7 ? 'text-red-400' :
                    selectedPoint.risk_score > 0.4 ? 'text-amber-400' : 'text-green-400'
                  }`}>
                    {(selectedPoint.risk_score * 100).toFixed(0)}%
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Coordonnees</div>
                <div className="text-sm font-mono text-gray-400">
                  {selectedPoint.lat.toFixed(4)}, {selectedPoint.lng.toFixed(4)}
                </div>
              </div>

              {selectedPoint.details && (
                <div>
                  <div className="text-xs text-gray-500">Details</div>
                  <div className="text-sm text-gray-300">{selectedPoint.details}</div>
                </div>
              )}

              {/* Sources */}
              {selectedPoint.sources && selectedPoint.sources.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Sources</div>
                  <div className="space-y-1">
                    {selectedPoint.sources.map((src, i) => (
                      <a
                        key={i}
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-cyber-cyan hover:text-cyan-300 hover:underline"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                        </svg>
                        {src.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 space-y-2">
                <button className="w-full px-3 py-2 rounded-lg bg-cyber-cyan/10 border border-cyber-cyan/20 text-cyber-cyan text-xs font-medium hover:bg-cyber-cyan/20 transition-colors">
                  Investiguer cette entite
                </button>
                <button className="w-full px-3 py-2 rounded-lg bg-surface-tertiary border border-border-subtle text-gray-400 text-xs font-medium hover:text-gray-200 transition-colors">
                  Voir dans le graphe
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
